import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { ajax_wrapper } from 'functions'; // Assuming ajax_wrapper exists for API calls

// Utility function to get the base URL (adjust if needed)
const getBaseUrl = () => {
    const { protocol, host } = window.location;
    return `${protocol}//${host}`;
};

const ReferralModal = ({
    show,
    handleClose,
    headline,
    referrerId, // The current user's ID (UUID)
    referralMessage = '', // Optional message for email/copy
    children, // To allow embedding components like address search
}) => {
    const [referralLink, setReferralLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [emailButtonAnimating, setEmailButtonAnimating] = useState(false);

    useEffect(() => {
        if (referrerId) {
            const baseUrl = getBaseUrl();
            // Generate the referral link directly on the frontend
            setReferralLink(`${baseUrl}/?ref=${referrerId}`);
        }
    }, [referrerId]);

    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            // Optionally show an error message to the user
        });
    };

    const handleShareEmail = () => {
        // Set animation state for better UX feedback
        setEmailButtonAnimating(true);
        
        // Prepare email data
        const subject = encodeURIComponent("Check out HomeBase!");
        let bodyContent = `Hey!\n\nI thought you might be interested in checking out HomeBase.`;
        if (referralMessage) {
             bodyContent += ` ${referralMessage}` // Append custom message if provided
        }
        bodyContent += `\n\nYou can use this link to learn more: ${referralLink}\n\nBest,\n[Your Name]`; // User can replace [Your Name]
        const body = encodeURIComponent(bodyContent);
        
        // Reset animation state after delay
        setTimeout(() => {
            setEmailButtonAnimating(false);
            
            // Try using a more compatible approach for different OS platforms
            try {
                // First approach: Standard mailto link
                const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
                
                // Create a hidden anchor element and trigger a click
                const link = document.createElement('a');
                link.href = mailtoLink;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                setTimeout(() => {
                    document.body.removeChild(link);
                }, 100);
            } catch (e) {
                console.error("Error opening email client:", e);
                
                // Fallback for platforms where the first approach fails
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
            }
        }, 300);
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{headline}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {children} {/* Render embedded components here */}
                {referralLink && (
                    <>
                        <p>Share this link with friends and family:</p>
                        <InputGroup className="mb-3">
                            <Form.Control
                                type="text"
                                value={referralLink}
                                readOnly
                                aria-label="Referral Link"
                            />
                            <Button variant="outline-secondary" onClick={handleCopy}>
                                {copied ? 'Copied!' : 'Copy Link'}
                            </Button>
                        </InputGroup>
                        <Button 
                            variant="primary" 
                            onClick={handleShareEmail} 
                            className="me-2"
                            disabled={emailButtonAnimating}
                            style={{
                                transition: 'all 0.3s ease',
                                transform: emailButtonAnimating ? 'scale(1.05)' : 'scale(1)',
                                opacity: emailButtonAnimating ? '0.9' : '1',
                            }}
                        >
                            Share via Email
                        </Button>
                    </>
                )}
                 {!referralLink && !children && (
                     <p>Loading referral information...</p> // Or handle cases where referrerId is missing
                 )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReferralModal; 